import * as coreClient from "@azure/core-client";
import * as coreRestPipeline from "@azure/core-rest-pipeline";

export interface CreateCustomerRequest {
  /** Parent Account ID (if applicable) */
  parentAccountId?: string;
  /** Customer's First Name */
  firstName: string;
  /** Customer's Last Name */
  lastName: string;
  /** Customer's Title */
  title?: string;
  /** Customer's Email ID */
  emailAddress: string;
  /** Customer's Date Of Birth - ISO 8601 Date Format */
  dateOfBirth: string;
  /** Customer's Contact Number */
  telephoneNumber?: string;
  /** Customer's Status (as Status is optional, if not provided when Customer Object is created it is set to 'active' as default) */
  status?: CreateCustomerRequestStatus;
  marketingPreferences: MarketingPreference[];
  address: Address;
  bankAccount?: BankAccount;
}

export interface MarketingPreference {
  name?: MarketingPreferenceName;
  optIn?: boolean;
}

export interface Address {
  department?: string;
  company?: string;
  /** First Address Line */
  address1: string;
  /** Second Address Line */
  address2?: string;
  address3?: string;
  /** Locality or Town */
  town: string;
  /** County */
  county?: string;
  /** Postal Code */
  postCode: string;
  /** Country */
  country?: string;
  /** Local Authority */
  localAuthority?: string;
}

export interface BankAccount {
  /** Bank Full Name */
  bankName: string;
  /** Bank Branch Name */
  branchName?: string;
  /** Account Number */
  accountNumber: string;
  /** Sort Code */
  sortCode: string;
}

export interface Customer {
  /** Parent Account ID (if applicable) */
  parentAccountId?: string;
  /** Unique Customer ID */
  id: string;
  /** Customer's First Name */
  firstName: string;
  /** Customer's Last Name */
  lastName: string;
  /** Customer's Title */
  title?: string;
  /** Customer's Email ID */
  emailAddress: string;
  /** Customer's Date Of Birth - ISO 8601 Date Format */
  dateOfBirth: string;
  /** Customer's Contact Number */
  telephoneNumber?: string;
  /** Customer's Status */
  status: CustomerStatus;
  marketingPreferences: MarketingPreference[];
  address: Address;
  /** Record Created Date - ISO 8601 Date Format */
  createdDate?: string;
  bankAccount?: BankAccount;
}

export interface ApiError {
  code?: number;
  shortDescription?: string;
  description?: string;
}

export interface UpdateCustomerRequest {
  /** Parent Account ID (if applicable) */
  parentAccountId?: string;
  /** Customer's First Name */
  firstName: string;
  /** Customer's Last Name */
  lastName: string;
  /** Customer's Title */
  title?: string;
  /** Customer's Email ID */
  emailAddress: string;
  /** Customer's Date Of Birth - ISO 8601 Date Format */
  dateOfBirth: string;
  /** Customer's Contact Number */
  telephoneNumber?: string;
  /** Customer's Status (as Status is optional, if not provided it will remain as is when Customer Object was created) */
  status?: UpdateCustomerRequestStatus;
  marketingPreferences: MarketingPreference[];
  address: Address;
  bankAccount?: BankAccount;
}

export interface CustomerProfileUploadResponse {
  imageResource?: string;
  /** Date Uploaded - ISO 8601 Date Format */
  dateUploaded?: string;
}

export interface MobileAppVerifyRequest {
  /** Customer or Applicant Email ID */
  emailAddress: string;
}

export interface MobileAppVerifyStatus {
  /** Mobile App Verification Status */
  status?: "available";
}

export interface SwiftCardCheckStatus {
  /** SwiftCard Verification Status */
  status?: SwiftCardCheckStatusStatus;
}

export interface ApplicantCustomer {
  /** Unique Applicant Customer ID */
  id: string;
  /** Applicant Customer's Title */
  title: string;
  /** Applicant Customer's First Name */
  firstName: string;
  /** Applicant Customer's Last Name */
  lastName: string;
  /** Applicant Customer's Email ID */
  emailAddress: string;
  /** Applicant Customer's Date Of Birth - ISO 8601 Date Format */
  dateOfBirth: string;
}

export interface ApplicantSwiftcardsRequest {
  /** Billing Customer Email Address */
  billingCustomerEmail: string;
  /** Unique Applicant Customer ID (Must be Passed for Selected Applicant, optional for Self or New Applicant) */
  applicantCustomerID?: string;
  /** Ticket Product ID */
  ticketID: number;
}

export interface CustomerSwiftCard {
  /** Swiftcard Number */
  cardNumber: string;
}

/** Known values of {@link HostOptions} that the service accepts. */
export enum KnownHostOptions {
  /** host: https://nxbus.api.nationalexpress.com */
  HttpsNxbusApiNationalexpressCom = "https://nxbus.api.nationalexpress.com",
  /** host: https://perfnxbus.api.nationalexpress.com */
  HttpsPerfnxbusApiNationalexpressCom = "https://perfnxbus.api.nationalexpress.com",
  /** host: https://dt8nxbus.api.nationalexpress.com */
  HttpsDt8NxbusApiNationalexpressCom = "https://dt8nxbus.api.nationalexpress.com",
  /** host: https://dt6nxbus.api.nationalexpress.com */
  HttpsDt6NxbusApiNationalexpressCom = "https://dt6nxbus.api.nationalexpress.com"
}

/**
 * Defines values for HostOptions. \
 * {@link KnownHostOptions} can be used interchangeably with HostOptions,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **https:\/\/nxbus.api.nationalexpress.com**: host: https:\/\/nxbus.api.nationalexpress.com \
 * **https:\/\/perfnxbus.api.nationalexpress.com**: host: https:\/\/perfnxbus.api.nationalexpress.com \
 * **https:\/\/dt8nxbus.api.nationalexpress.com**: host: https:\/\/dt8nxbus.api.nationalexpress.com \
 * **https:\/\/dt6nxbus.api.nationalexpress.com**: host: https:\/\/dt6nxbus.api.nationalexpress.com
 */
export type HostOptions = string;

/** Known values of {@link CreateCustomerRequestStatus} that the service accepts. */
export enum KnownCreateCustomerRequestStatus {
  /** Active */
  Active = "active",
  /** Inactive */
  Inactive = "inactive"
}

/**
 * Defines values for CreateCustomerRequestStatus. \
 * {@link KnownCreateCustomerRequestStatus} can be used interchangeably with CreateCustomerRequestStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **active** \
 * **inactive**
 */
export type CreateCustomerRequestStatus = string;

/** Known values of {@link MarketingPreferenceName} that the service accepts. */
export enum KnownMarketingPreferenceName {
  /** Email */
  Email = "email",
  /** Post */
  Post = "post",
  /** Mobile */
  Mobile = "mobile"
}

/**
 * Defines values for MarketingPreferenceName. \
 * {@link KnownMarketingPreferenceName} can be used interchangeably with MarketingPreferenceName,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **email** \
 * **post** \
 * **mobile**
 */
export type MarketingPreferenceName = string;

/** Known values of {@link CustomerStatus} that the service accepts. */
export enum KnownCustomerStatus {
  /** Active */
  Active = "active",
  /** Inactive */
  Inactive = "inactive"
}

/**
 * Defines values for CustomerStatus. \
 * {@link KnownCustomerStatus} can be used interchangeably with CustomerStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **active** \
 * **inactive**
 */
export type CustomerStatus = string;

/** Known values of {@link UpdateCustomerRequestStatus} that the service accepts. */
export enum KnownUpdateCustomerRequestStatus {
  /** Active */
  Active = "active",
  /** Inactive */
  Inactive = "inactive"
}

/**
 * Defines values for UpdateCustomerRequestStatus. \
 * {@link KnownUpdateCustomerRequestStatus} can be used interchangeably with UpdateCustomerRequestStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **active** \
 * **inactive**
 */
export type UpdateCustomerRequestStatus = string;

/** Known values of {@link SwiftCardCheckStatusStatus} that the service accepts. */
export enum KnownSwiftCardCheckStatusStatus {
  /** Verified */
  Verified = "verified",
  /** Unverified */
  Unverified = "unverified"
}

/**
 * Defines values for SwiftCardCheckStatusStatus. \
 * {@link KnownSwiftCardCheckStatusStatus} can be used interchangeably with SwiftCardCheckStatusStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **verified** \
 * **unverified**
 */
export type SwiftCardCheckStatusStatus = string;
/** Defines values for ContentType. */
export type ContentType = "image/jpeg" | "image/png";

/** Optional parameters. */
export interface CreateCustomerAccountOptionalParams
  extends coreClient.OperationOptions {
  /** The Create Customer Request Object */
  body?: CreateCustomerRequest;
}

/** Contains response data for the createCustomerAccount operation. */
export type CreateCustomerAccountResponse = Customer;

/** Optional parameters. */
export interface GetCustomerAccountOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getCustomerAccount operation. */
export type GetCustomerAccountResponse = Customer;

/** Optional parameters. */
export interface UpdateCustomerAccountOptionalParams
  extends coreClient.OperationOptions {
  /** The Update Customer Request Object */
  body?: UpdateCustomerRequest;
}

/** Contains response data for the updateCustomerAccount operation. */
export type UpdateCustomerAccountResponse = Customer;

/** Optional parameters. */
export interface UploadCustomerAccountPhotoOptionalParams
  extends coreClient.OperationOptions {
  /** Upload file type */
  contentType?: ContentType;
  /** Customer Account Profile Image Upload Request */
  data?: coreRestPipeline.RequestBodyType;
}

/** Contains response data for the uploadCustomerAccountPhoto operation. */
export type UploadCustomerAccountPhotoResponse = CustomerProfileUploadResponse;

/** Optional parameters. */
export interface CustomerAccountProfileImageOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the customerAccountProfileImage operation. */
export type CustomerAccountProfileImageResponse = {
  /**
   * BROWSER ONLY
   *
   * The response body as a browser Blob.
   * Always `undefined` in node.js.
   */
  blobBody?: Promise<Blob>;
  /**
   * NODEJS ONLY
   *
   * The response body as a node.js Readable stream.
   * Always `undefined` in the browser.
   */
  readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface MobileAppVerifyOptionalParams
  extends coreClient.OperationOptions {
  /** Mobile-App Verify Request Object */
  body?: MobileAppVerifyRequest;
}

/** Contains response data for the mobileAppVerify operation. */
export type MobileAppVerifyResponse = MobileAppVerifyStatus;

/** Optional parameters. */
export interface CheckSwiftCardOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the checkSwiftCard operation. */
export type CheckSwiftCardResponse = SwiftCardCheckStatus;

/** Optional parameters. */
export interface GetCustomerApplicantsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getCustomerApplicants operation. */
export type GetCustomerApplicantsResponse = ApplicantCustomer[];

/** Optional parameters. */
export interface GetApplicantSwiftcardsOptionalParams
  extends coreClient.OperationOptions {
  /** Get Applicant Swiftcards Request Object */
  body?: ApplicantSwiftcardsRequest;
}

/** Contains response data for the getApplicantSwiftcards operation. */
export type GetApplicantSwiftcardsResponse = CustomerSwiftCard[];

/** Optional parameters. */
export interface CustomerDetailsAPIOptionalParams
  extends coreClient.ServiceClientOptions {
  /** server parameter */
  $host?: HostOptions;
  /** Overrides client endpoint. */
  endpoint?: string;
}
